import DOMPurify, {
	type Config as DOMPurifyConfig,
	type DOMPurify as DOMPurifyInstance
} from "dompurify";
import {
	EMPTY_OBJECT,
	ensureNotNil,
	isNilOrWhiteSpace,
	isNotNil
} from "./utils";
import { never } from "client_legacy/ndaw/errors";

export function htmlToPlainText(html: string): string {
	const parser = new DOMParser();

	const document: Document = parser.parseFromString(
		sanitizeHtml(
			html,
			{ globalStylesBehavior: "remove" }
		),
		"text/html"
	);

	return document.body
		.innerText;
}

export function adjustTargetAndRelForAnchors(html: string): string {
	return html.replace(/<a[^>]*/g, function (a) {
		// first remove existing target attribute and rel attributes
		a = a.replace(/target\s*=\s*['"][^"']*['"]/, "").replace(/rel\s*=\s*['"][^"']*['"]/, "");

		// then add a target and rel attribute
		a += ` target="_blank" rel="noopener noreferrer"`;

		return a;
	});
}

const htmlCharactersEscapeMap = {
	["&"]: "&amp;",
	["<"]: "&lt;",
	[">"]: "&gt;",
	[`"`]: "&quot;",
	["'"]: "&#x27;"
} as const;

export function htmlEscape(value: string | nil): string {
	if (!value) {
		return "";
	}

	return value.replace(/[&<>"']/g, (c) => htmlCharactersEscapeMap[c as keyof typeof htmlCharactersEscapeMap]);
}

export type GlobalStylesBehavior =
	| "keep"
	| "remove"
	| {
		kind: "scope-to-selector";
		selector: string;
	};

export type SanitizeHtmlOptions = {
	globalStylesBehavior?: GlobalStylesBehavior;
};

export function sanitizeHtml(
	input: string,
	options: SanitizeHtmlOptions = EMPTY_OBJECT
): string {
	const dompurify: DOMPurifyInstance = DOMPurify(window);

	if (!dompurify.isSupported) {
		throw new Error("DOMPurify is not supported in this environment.");
	}

	dompurify.addHook("afterSanitizeAttributes", (node) => {
		if (!(node instanceof HTMLAnchorElement)) {
			return;
		}

		node.setAttribute("rel", "noopener noreferrer");
	});

	const {
		globalStylesBehavior = "keep"
	} = options;

	let config: DOMPurifyConfig = {
		ADD_ATTR: [
			// Allow the target attribute, but using the noopener/noreferrer hook we make sure
			// no window hijacking can take place
			"target"
		],
		FORBID_ATTR: [
			"class"
		]
	};

	if (typeof globalStylesBehavior === "string") {
		switch (globalStylesBehavior) {
			case "keep":
				break;
			case "remove":
				config = addForbiddenTagToDomPurifyConfig(config, "style");
				break;
			default:
				never(globalStylesBehavior);
		}
	} else {
		switch (globalStylesBehavior.kind) {
			case "scope-to-selector":
				dompurify.addHook(
					"beforeSanitizeElements",
					scopeStylesToSelectorHook(globalStylesBehavior.selector)
				);
				break;
			default:
				never(globalStylesBehavior.kind);
		}
	}

	return dompurify.sanitize(
		input,
		config
	);
}

export function sanitizeSvg(input: string): string {
	const dompurify: DOMPurifyInstance = DOMPurify(window);

	dompurify.addHook("afterSanitizeAttributes", (node) => {
		if (!(node instanceof HTMLAnchorElement)) {
			return;
		}

		node.setAttribute("rel", "noopener noreferrer");
	});

	if (!dompurify.isSupported) {
		throw new Error("DOMPurify is not supported in this environment.");
	}

	return dompurify.sanitize(input);
}

function scopeStylesToSelectorHook(nodeSelector: string): (node: Node) => void {
	if (isNilOrWhiteSpace(nodeSelector)) {
		throw new Error("Scope selector is not specified.");
	}

	return (node) => {
		if (!(node instanceof HTMLStyleElement)) {
			return;
		}

		const parsedStyleSheet = new CSSStyleSheet({
			disabled: true
		});

		try {
			parsedStyleSheet.replaceSync(node.innerHTML);
		} catch(e) {
			node.innerHTML = "";
			return;
		}

		try {
			const resultStyleSheet = new CSSStyleSheet();

			resultStyleSheet.insertRule(`@scope (${nodeSelector}) {}`);

			const scopeRule: CSSRule | null = resultStyleSheet.cssRules
				.item(0);

			if (!(scopeRule instanceof CSSScopeRule)) {
				throw new Error("Expected parsed style sheet to only contain a single css scope rule.");
			}

			for (let i = 0; i < parsedStyleSheet.cssRules.length; i++) {
				const rule: CSSRule = ensureNotNil(
					parsedStyleSheet.cssRules
						.item(i)
				);

				scopeRule.insertRule(rule.cssText, i);
			}

			node.innerHTML = scopeRule.cssText;
		} catch(e) {
			node.innerHTML = "";
			console.error("Unable to scope global styles, the styles will be omitted.", e);

			return;
		}
	};
}

function addForbiddenTagToDomPurifyConfig(config: DOMPurifyConfig, tag: string): DOMPurifyConfig {
	if (isNotNil(config.FORBID_TAGS) && config.FORBID_TAGS.includes(tag)) {
		return config;
	}

	const forbiddenTags: string[] = isNotNil(config.FORBID_TAGS)
		? [
			...config.FORBID_TAGS
		]
		: [];

	forbiddenTags.push(tag);

	return {
		...config,
		FORBID_TAGS: forbiddenTags
	};
}